import { useQuery } from "@tanstack/react-query";
import { type SkillFilters, skillKeys } from "@/lib/query";
import type { Category, RiskLevel, SkillListItem } from "@/lib/skills/types";

export interface SkillsListResponse {
  data: SkillListItem[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

interface PageRequest {
  cursor: string | null;
  direction: "next" | "prev";
}

export function useSkills(filters: SkillFilters, page: PageRequest) {
  return useQuery({
    queryKey: [...skillKeys.list(filters), page] as const,
    queryFn: async (): Promise<SkillsListResponse> => {
      const p = new URLSearchParams();
      p.set("limit", "24");
      if (page.cursor) {
        p.set("cursor", page.cursor);
        p.set("direction", page.direction);
      }
      if (filters.search) p.set("search", filters.search);
      if (filters.categories?.length) p.set("category", filters.categories.join(","));
      if (filters.risks?.length) p.set("risk", filters.risks.join(","));
      if (typeof filters.minRating === "number") p.set("minRating", String(filters.minRating));
      if (typeof filters.freshness === "number") p.set("freshness", String(filters.freshness));
      if (filters.authors?.length) p.set("author", filters.authors.join(","));
      if (filters.sort) p.set("sort", filters.sort);
      const res = await fetch(`/api/skills?${p}`);
      if (!res.ok) throw new Error("Failed to fetch skills");
      return res.json();
    },
  });
}

export type { Category, RiskLevel };

/** Fetch the whole catalog (published set, role-aware on the server) for client-side faceting. */
export function useSkillCatalog(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [...skillKeys.all, "catalog"] as const,
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<SkillListItem[]> => {
      const res = await fetch("/api/skills?limit=100");
      if (!res.ok) throw new Error("Failed to fetch catalog");
      const json: { data: SkillListItem[] } = await res.json();
      return json.data;
    },
  });
}
